import { ClientError } from '@ricoh/ria.client.core/errors';
import { flatItemMapper } from '@ricoh/ria.client.core/utils/mapper/flat-item';

import type { ImageEnhanceType, ScanningOptions } from '~/common/model/api/scanning-profile';

import { IMAGE_ENHANCE_TYPES } from '~/common/constants/feature/process/source-settings';

import type { TransformScanningOptions } from './_scanning.type';

/**
 * Prepare ScanningOptions.
 *
 * @param {TransformScanningOptions} options Transform ScanningOptions.
 *
 * @returns {ScanningOptions} ScanningOptions.
 */
export function prepareScanningOptions({ general, profile }: TransformScanningOptions): ScanningOptions {
  return {
    general: {
      showUI: general.showUI ?? false,
      resolution: general.resolution ?? 300,
      sides: general.sides ?? 'singleSided',
    },
    profile: {
      colorType: profile.colorType ?? 'auto',
      imageEnhanceType: flatItemMapper.prepare<ImageEnhanceType>(profile.imageEnhanceType, {
        emptyItem: 'none',
        transform: (type) => {
          if (IMAGE_ENHANCE_TYPES.includes(type)) {
            return type;
          }

          throw new ClientError({
            module: 'service',
            message: 'Invalid Image Enhance Type',
            cause: { type },
          });
        },
      }),
    },
  };
}

/**
 * Transform ScanningOptions.
 *
 * @param {ScanningOptions} options ScanningOptions.
 *
 * @returns {TransformScanningOptions} Transformed ScanningOptions.
 */
export function transformScanningOptions({ general, profile }: ScanningOptions): TransformScanningOptions {
  const imageEnhanceType = profile.imageEnhanceType?.length > 0 ? profile.imageEnhanceType.join(', ') : 'none';

  return {
    general,
    profile: {
      ...profile,
      imageEnhanceType,
    },
  };
}
